import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { usePlaces } from '@/hooks/usePlaces';
import { useFavorites } from '@/hooks/useFavorites';
import { locationConfig } from '@shared/location';
import { RanchMapToolbar } from '@/components/RanchMapToolbar';
import { RanchMapSheet, type SheetSnap } from '@/components/RanchMapSheet';
import {
  activeMapCategories,
  filterMapPlaces,
  isCorePlace,
  isMappablePlace,
  mapCategoryMeta,
  nearbyList,
  parseMapFilters,
  serializeMapFilters,
  type MapBounds,
} from '@/lib/ranchMap';
/**
 * Map-first spatial view of in-Ranch places.
 *
 * Category chips (Places-style, single-select) replace the old floating Key
 * panel. Pins use leaflet.markercluster so dense corridors are not pin soup;
 * co-located rows (Founder's Square, the schools campus) spiderfy at max
 * zoom instead of the old fan-out. A collapsed Nearby sheet lists places
 * in the current viewport; pin tap peeks a card with Directions / details.
 */

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

function readVar(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || '#173D4E';
}

function pinIcon(category: string, selected: boolean): L.DivIcon {
  const color = readVar(mapCategoryMeta(category).cssVar);
  return L.divIcon({
    className: 'ranch-map-pin',
    html: `<div class="ranch-map-pin__dot${selected ? ' ranch-map-pin__dot--on' : ''}" style="background:${color}"></div>`,
    iconSize: selected ? [22, 22] : [16, 16],
    iconAnchor: selected ? [11, 11] : [8, 8],
  });
}

function visibleBounds(map: L.Map, sheetPx: number, topPx: number): MapBounds {
  const size = map.getSize();
  const bottom = Math.max(topPx + 40, size.y - sheetPx);
  const sw = map.containerPointToLatLng(L.point(0, bottom));
  const ne = map.containerPointToLatLng(L.point(size.x, topPx));
  return { south: sw.lat, west: sw.lng, north: ne.lat, east: ne.lng };
}

const SHEET_PX: Record<SheetSnap, number> = {
  collapsed: 72,
  peek: 248,
  expanded: 420,
};

export function RanchMapPage() {
  const { places } = usePlaces();
  const { isFavorite, toggle } = useFavorites();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const initial = useMemo(() => parseMapFilters(search), [search]);

  const [category, setCategory] = useState(initial.category);
  const [query, setQuery] = useState(initial.query);
  const [snap, setSnap] = useState<SheetSnap>('collapsed');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [bounds, setBounds] = useState<MapBounds | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);
  const markersRef = useRef<Map<number, L.Marker>>(new Map());
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const fittedRef = useRef(false);

  useEffect(() => {
    setCategory(initial.category);
    setQuery(initial.query);
  }, [initial]);

  useEffect(() => {
    const nextSearch = serializeMapFilters(category, query);
    if (nextSearch !== search.replace(/^\?/, '')) {
      navigate({ pathname, search: nextSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, query]);

  const inRanch = useMemo(() => (places ?? []).filter(isMappablePlace), [places]);

  const filtered = useMemo(
    () => filterMapPlaces(inRanch, { category, query }),
    [inRanch, category, query],
  );

  const chips = useMemo(() => activeMapCategories(inRanch), [inRanch]);

  const listed = useMemo(() => nearbyList(filtered, bounds, query), [filtered, bounds, query]);

  const selected = useMemo(
    () => (selectedId == null ? null : (filtered.find((p) => p.id === selectedId) ?? null)),
    [filtered, selectedId],
  );

  const publishBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    // Wrapped chip rows make the overlay taller than the old single
    // scroll-row (~132px). Measure so Nearby subtracts the real chrome.
    const topPx = toolbarRef.current?.offsetHeight ?? 176;
    setBounds(visibleBounds(map, SHEET_PX[snapRef.current], topPx));
  }, []);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const { homeBase, zoomDefault } = locationConfig.geo;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      scrollWheelZoom: true,
    }).setView([homeBase.lat, homeBase.lon], zoomDefault);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
    }).addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    const star = L.divIcon({
      className: 'ranch-map-home',
      html: `<div class="ranch-map-home__star" title="${escapeHtml(homeBase.label)}">★</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
    L.marker([homeBase.lat, homeBase.lon], {
      icon: star,
      zIndexOffset: 1000,
      keyboard: false,
    })
      .addTo(map)
      .bindPopup(`<strong>${escapeHtml(homeBase.label)}</strong><br>You are here`);

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      spiderfyDistanceMultiplier: 1.8,
      maxClusterRadius: 48,
      chunkedLoading: true,
      iconCreateFunction: (c) =>
        L.divIcon({
          className: 'ranch-map-cluster-wrap',
          html: `<div class="ranch-map-cluster">${c.getChildCount()}</div>`,
          iconSize: [36, 36],
          iconAnchor: [18, 18],
        }),
    });
    cluster.addTo(map);
    clusterRef.current = cluster;
    const markers = markersRef.current;

    map.on('moveend', publishBounds);
    map.on('zoomend', publishBounds);
    map.on('click', () => {
      setSelectedId(null);
      setSnap((s) => (s === 'peek' ? 'collapsed' : s));
    });

    mapRef.current = map;
    requestAnimationFrame(() => {
      map.invalidateSize();
      publishBounds();
    });

    return () => {
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
      markers.clear();
      fittedRef.current = false;
    };
  }, [publishBounds]);

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => publishBounds());
    ro.observe(el);
    return () => ro.disconnect();
  }, [publishBounds]);

  useEffect(() => {
    const map = mapRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster) return;

    cluster.clearLayers();
    markersRef.current.clear();

    for (const place of filtered) {
      const marker = L.marker([place.latitude, place.longitude], {
        icon: pinIcon(place.category, false),
        title: place.name,
      });
      marker.on('click', () => {
        setSelectedId(place.id);
        setSnap('peek');
      });
      cluster.addLayer(marker);
      markersRef.current.set(place.id, marker);
    }
  }, [filtered]);

  useEffect(() => {
    for (const [id, marker] of markersRef.current) {
      const place = filtered.find((p) => p.id === id);
      if (place) marker.setIcon(pinIcon(place.category, id === selectedId));
    }
  }, [filtered, selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || fittedRef.current || inRanch.length === 0) return;
    const { homeBase } = locationConfig.geo;
    const core = inRanch.filter(isCorePlace);
    const framing = core.length > 0 ? core : inRanch;
    const pts: L.LatLngExpression[] = [
      [homeBase.lat, homeBase.lon],
      ...framing.map((p) => [p.latitude, p.longitude] as [number, number]),
    ];
    map.fitBounds(L.latLngBounds(pts), { padding: [48, 48], maxZoom: 15 });
    fittedRef.current = true;
  }, [inRanch]);

  useEffect(() => {
    const page = containerRef.current?.parentElement;
    if (page) page.style.setProperty('--ranch-map-sheet-height', `${SHEET_PX[snap]}px`);
    const map = mapRef.current;
    if (map) {
      requestAnimationFrame(() => {
        map.invalidateSize();
        publishBounds();
      });
    }
  }, [snap, publishBounds]);

  const fitAll = () => {
    const map = mapRef.current;
    if (!map) return;
    const { homeBase } = locationConfig.geo;
    const pts: L.LatLngExpression[] = [
      [homeBase.lat, homeBase.lon],
      ...filtered.map((p) => [p.latitude, p.longitude] as [number, number]),
    ];
    map.fitBounds(L.latLngBounds(pts), { padding: [48, 48] });
  };

  const onSelect = (place: { id: number }) => {
    setSelectedId(place.id);
    setSnap('peek');
    const map = mapRef.current;
    const marker = markersRef.current.get(place.id);
    if (map && marker) {
      const cluster = clusterRef.current;
      if (cluster && 'zoomToShowLayer' in cluster) {
        cluster.zoomToShowLayer(marker, () => {
          map.panTo(marker.getLatLng());
        });
      } else {
        map.flyTo(marker.getLatLng(), Math.max(map.getZoom(), 17), { duration: 0.5 });
      }
    }
  };

  return (
    <div className="ranch-map-page">
      <h1 className="visually-hidden">Map</h1>
      <div ref={containerRef} className="ranch-map" aria-label="Map of Babcock Ranch" />

      <RanchMapToolbar
        toolbarRef={toolbarRef}
        categories={chips}
        category={category}
        onCategory={(key) => {
          setCategory(key);
          setSelectedId(null);
        }}
        query={query}
        onQuery={(value) => {
          setQuery(value);
          setSelectedId(null);
        }}
        onFitAll={fitAll}
      />

      <RanchMapSheet
        snap={snap}
        onSnap={setSnap}
        places={listed}
        selected={selected}
        onSelect={onSelect}
        isFavorite={isFavorite}
        onToggleFavorite={toggle}
      />
    </div>
  );
}
